/**
 * Export menu (OutlierOS "Export Dataset ▾", docs/legacy-ui/02 §1 C): CSV, Excel, Google Sheets (TSV copied +
 * downloaded), JSON and Notion Markdown, plus "with transcripts" variants that transcribe first. Columns come from
 * packages/core/src/export-columns.ts; the plan's export row limit (admin-editable) is applied.
 */
import { useEffect, useRef, useState } from 'react';
import { ChevronDown, Download, Mic } from 'lucide-react';
import { BRAND } from '@vw/core/src/brand';
import { TRANSCRIPTS_MAX_PER_REQUEST } from '@vw/core/src/limits';
import { toCsv, toTsv } from '@vw/core/src/csv';
import { EXPORT_COLUMNS, exportJson, exportRow, toNotionMarkdown, type ExportTranscript } from '@vw/core/src/export-columns';
import type { ReportReel } from '@vw/core/src/report';
import { Button, toast, type TierThresholds } from '@vw/ui';
import { api } from '../../api';
import { downloadText, stamp } from '../../download';
import { navigate } from '../../router';

export type ExportFormat = 'csv' | 'xlsx' | 'tsv' | 'json' | 'notion';
export const FORMAT_LABEL: Record<ExportFormat, string> = { csv: 'CSV', xlsx: 'Excel (.xlsx)', tsv: 'Google Sheets (copy)', json: 'JSON', notion: 'Notion Markdown' };

export async function fetchTranscripts(pks: string[]): Promise<Record<string, ExportTranscript>> {
  const out: Record<string, ExportTranscript> = {};
  for (let i = 0; i < pks.length; i += TRANSCRIPTS_MAX_PER_REQUEST) {
    const r = await api<{ transcripts: Record<string, ExportTranscript> }>('/api/transcripts', { method: 'POST', json: { reelPks: pks.slice(i, i + TRANSCRIPTS_MAX_PER_REQUEST) } });
    Object.assign(out, r.transcripts);
  }
  return out;
}

export async function runExport(format: ExportFormat, rows: ReportReel[], o: {
  handle: string; fullName: string | null; tiers: TierThresholds; limit: number | null; withTranscripts: boolean;
  /** Reels transcribed since `rows` was loaded ("Transcribe & export"). */
  alsoTranscribed?: string[];
}) {
  let list = rows;
  if (o.limit !== null && rows.length > o.limit) {
    list = rows.slice(0, o.limit);
    toast.info(`Exported the first ${o.limit} of ${rows.length} rows`, { description: 'Your plan limits exports. Upgrade to export everything.', durationMs: 10_000, action: { label: 'See plans', onClick: () => navigate('/billing') } });
  }
  const extra = new Set(o.alsoTranscribed ?? []);
  const transcripts = o.withTranscripts ? await fetchTranscripts(list.filter((r) => r.hasTranscript || extra.has(r.pk)).map((r) => r.pk)) : undefined;
  const creator = { handle: o.handle, fullName: o.fullName };
  const now = Date.now();
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  const base = `${o.handle}_wise_score_reels_${stamp()}`;
  const data = list.map((r) => exportRow(r, creator, { tiers: o.tiers, timeZone, now, transcript: transcripts?.[r.pk] }));
  if (format === 'csv') downloadText(`${base}.csv`, toCsv([...EXPORT_COLUMNS], data), 'text/csv');
  else if (format === 'tsv') {
    const tsv = toTsv([...EXPORT_COLUMNS], data);
    try {
      await navigator.clipboard.writeText(tsv);
      toast.success('Copied: paste into Google Sheets', { description: 'A .tsv file was downloaded too.' });
    } catch { /* download only */ }
    downloadText(`${base}.tsv`, tsv, 'text/tab-separated-values');
  } else if (format === 'json') downloadText(`${base}.json`, JSON.stringify(exportJson(list, creator, { tiers: o.tiers, timeZone, now, transcripts }), null, 2), 'application/json');
  else if (format === 'notion') downloadText(`${o.handle}-${BRAND.slug}-notion.md`, toNotionMarkdown(list, creator, { brand: BRAND.name, now, transcripts }), 'text/markdown');
  else {
    const XLSX = await import('xlsx');
    const ws = XLSX.utils.aoa_to_sheet([[...EXPORT_COLUMNS], ...data]);
    ws['!cols'] = EXPORT_COLUMNS.map((c) => ({ wch: Math.min(50, Math.max(c.length + 2, 12)) }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Wise Score Reels');
    XLSX.writeFile(wb, `${base}.xlsx`);
  }
}

export function ExportMenu({ disabled, onExport, onTranscribeExport, busy }: { disabled: boolean; busy: boolean; onExport: (f: ExportFormat) => void; onTranscribeExport: (f: ExportFormat) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && setOpen(false);
    const key = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', close);
    window.addEventListener('keydown', key);
    return () => { document.removeEventListener('mousedown', close); window.removeEventListener('keydown', key); };
  }, [open]);
  const item = (label: string, tag: string, fn: () => void, off = false) => (
    <button role="menuitem" disabled={off} onClick={() => { setOpen(false); fn(); }} className="flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2 text-left text-xs font-semibold hover:bg-secondary disabled:opacity-40">
      <span>{label}</span><span className="rounded bg-secondary px-1.5 py-0.5 text-[9px] font-bold uppercase text-muted-foreground">{tag}</span>
    </button>
  );
  return (
    <div className="relative" ref={ref}>
      <Button variant="secondary" disabled={disabled} onClick={() => setOpen(!open)} aria-expanded={open} aria-haspopup="menu"><Download className="h-3.5 w-3.5" />Export Dataset<ChevronDown className="h-3.5 w-3.5" /></Button>
      {open && (
        <div role="menu" className="absolute right-0 top-[calc(100%+6px)] z-40 w-64 animate-zoom-in rounded-2xl border border-border bg-card p-1.5 shadow-xl">
          {item('CSV', `${EXPORT_COLUMNS.length} columns`, () => onExport('csv'))}
          {item('Microsoft Excel', '.xlsx', () => onExport('xlsx'))}
          {item('Google Sheets', 'Copy', () => onExport('tsv'))}
          {item('Notion', 'Markdown', () => onExport('notion'))}
          {item('JSON', 'Data', () => onExport('json'))}
          <div className="my-1 border-t border-border" />
          <div className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground"><Mic className="h-3 w-3" />Transcribe & export</div>
          {item('CSV + transcripts', 'AI', () => onTranscribeExport('csv'), busy)}
          {item('Excel + transcripts', 'AI', () => onTranscribeExport('xlsx'), busy)}
          {item('Notion + transcripts', 'AI', () => onTranscribeExport('notion'), busy)}
        </div>
      )}
    </div>
  );
}
